import React, {useState} from "react";
import {
    getMultiFactorResolver,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    TotpMultiFactorGenerator
} from "firebase/auth";
import {auth} from "../../firebaseConfig";
import MFATOTPSetupStep from "./MFATOTPSetupStep";
import MFACodeStep from "./MFACodeStep";
import {Box, Button, Paper, Stack, TextField, Typography} from "@mui/material";
import ReferToEmailStep from "./ReferToEmailStep";


export interface PasswordStepProps {
    email: string;
}

const PasswordStep: React.FC<PasswordStepProps> = ({email}) => {
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string>("");
    const [nextStep, setNextStep] = useState<JSX.Element | null>(null);
    const [failedAttempts, setFailedAttempts] = useState(0);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (error) setError(""); // Clear error when user types
    };

    const handleSubmit = async () => {
        let mfaError: any = null;
        try {
            console.info("Trying to sign in for:", email);
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            if (!user.emailVerified) {
                console.info("Email is not verified.");
                setNextStep(
                    <Box>
                        <Stack spacing={2}>
                            <Paper elevation={0} sx={{p: 4, borderRadius: 0, m: 2, flexGrow: 1}}>
                                <Typography color="info">Welkom bij Medendo! 🚀 We sturen je eerst
                                    een <strong>e-mail</strong>, zodat je je eigen wachtwoord kunt
                                    instellen.</Typography>
                            </Paper>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    try {
                                        await sendPasswordResetEmail(auth, email);
                                        console.info("Password reset email sent successfully.");
                                        setNextStep(<ReferToEmailStep email={email}/>);
                                    } catch (error) {
                                        console.error("Failed to send password reset email:", error);
                                        setError("Er is een fout opgetreden bij het verzenden van de e-mail.");
                                    }
                                }}
                            >
                                Email versturen
                            </Button>
                        </Stack>
                    </Box>
                );
                return;
            }

            console.info("User has been signed in successfully");
            setNextStep(<MFATOTPSetupStep email={email} setStep={setNextStep}/>);
        } catch (error: any) {
            if (error.code === "auth/multi-factor-auth-required") {
                mfaError = error;
            } else {
                setError("Ongeldig wachtwoord. Probeer opnieuw.");
                setPassword("");
                setFailedAttempts(prev => prev + 1);
            }
        }

        if (!mfaError) return;
        console.info("2FA required");

        try {
            const mfaResolver = getMultiFactorResolver(auth, mfaError);
            const totpFactor = mfaResolver.hints.find(hint => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID);
            if (totpFactor) {
                console.info("TOTP found, switching to MFA code step");
                setNextStep(<MFACodeStep mfaResolver={mfaResolver} enrollmentId={totpFactor.uid}/>);
            } else {
                throw new Error("Unsupported MFA method detected. Only TOTP is currently supported.");
            }
        } catch (resolverError) {
            console.error("Failed to get MFA resolver:", resolverError);
            setError("Er is een fout opgetreden bij het verifiëren van uw account.");
        }
    };

    const handleForgotPassword = async () => {
        try {
            await sendPasswordResetEmail(auth, email);
            setError("Een e-mail voor het opnieuw instellen van uw wachtwoord is verzonden.");
            setNextStep(<ReferToEmailStep email={email}/>);
        } catch (error: any) {
            setError("Fout bij het verzenden van de e-mail: " + error.message);
        }
    };

    const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            await handleSubmit();
        }
    };

    if (nextStep) return nextStep;

    return (
        <Box>
            <Paper elevation={3} sx={{m: 2, flexGrow: 1}}>
                <Stack>
                    <Typography variant="h6">Wachtwoord</Typography>

                    <TextField
                        type="password"
                        label="Voer uw wachtwoord in"
                        variant="outlined"
                        value={password}
                        onChange={handleChange}
                        onKeyDown={handleKeyPress}
                        fullWidth
                    />

                    {error && <Typography color="error">{error}</Typography>}

                    <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!password || !!error}>
                        Inloggen
                    </Button>

                    {failedAttempts >= 1 && (
                        <Button
                            onClick={handleForgotPassword}
                            color="error"
                            sx={{textTransform: 'none'}}
                        >
                            Wachtwoord vergeten?
                        </Button>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default PasswordStep;
